// Phase 24A — Presets menu. Save / load / rename / delete / set-default.
// Loads presets for a given report_key; on default preset (if any) autoloads
// once on mount when caller passes `autoLoadDefault`.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, StarOff, Bookmark, ChevronDown, Trash2, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function PresetsMenu({ reportKey, filters, onApply, autoLoadDefault = true }) {
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDefault, setNewDefault] = useState(false);
  const [loadedDefault, setLoadedDefault] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/report-presets", { params: { report_key: reportKey } });
      setPresets(data.items || []);
      if (autoLoadDefault && !loadedDefault) {
        const def = (data.items || []).find(p => p.is_default);
        if (def) { onApply(def.filters || {}); setLoadedDefault(true); }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [reportKey, autoLoadDefault, loadedDefault, onApply]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!newName.trim()) { toast.error("Preset name required"); return; }
    setBusy(true);
    try {
      await api.post("/report-presets", {
        report_key: reportKey, name: newName.trim(),
        filters, is_default: newDefault,
      });
      toast.success("Preset saved");
      setSaveOpen(false); setNewName(""); setNewDefault(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  async function setDefault(p) {
    try {
      await api.patch(`/report-presets/${p.id}`, { is_default: !p.is_default });
      toast.success(p.is_default ? "Default cleared" : "Set as default");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function remove(p) {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/report-presets/${p.id}`);
      toast.success("Preset deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="flex gap-2" data-testid="presets-menu">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" data-testid="presets-menu-trigger">
            <Bookmark className="w-4 h-4 mr-1" /> Presets
            {presets.length > 0 && <span className="ml-1 text-xs text-neutral-500">({presets.length})</span>}
            <ChevronDown className="w-3 h-3 ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Saved presets</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {busy && (
            <DropdownMenuItem disabled><Loader2 className="w-3 h-3 animate-spin mr-2" /> Loading…</DropdownMenuItem>
          )}
          {!busy && presets.length === 0 && (
            <DropdownMenuItem disabled className="text-neutral-500">No saved presets yet</DropdownMenuItem>
          )}
          {!busy && presets.map(p => (
            <DropdownMenuItem key={p.id}
                                onSelect={(e) => { e.preventDefault(); onApply(p.filters || {}); toast.success(`Loaded: ${p.name}`); }}
                                className="flex items-center justify-between"
                                data-testid={`preset-item-${p.id}`}>
              <span className="flex items-center gap-2 flex-1 truncate">
                {p.is_default && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                <span className="truncate">{p.name}</span>
              </span>
              <span className="flex items-center gap-1">
                <button type="button" onClick={(e) => { e.stopPropagation(); setDefault(p); }}
                          className="text-neutral-400 hover:text-yellow-600"
                          title={p.is_default ? "Clear default" : "Set as default"}
                          data-testid={`preset-default-${p.id}`}>
                  {p.is_default ? <StarOff className="w-3 h-3" /> : <Star className="w-3 h-3" />}
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(p); }}
                          className="text-neutral-400 hover:text-red-600"
                          data-testid={`preset-delete-${p.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setSaveOpen(true); }}
                              data-testid="preset-save-open">
            <Save className="w-3 h-3 mr-2" /> Save current filters…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm" data-testid="preset-save-dialog">
          <DialogHeader>
            <DialogTitle>Save filters as preset</DialogTitle>
            <DialogDescription>Give this filter set a name so you can reload it later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Preset name</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)}
                       placeholder="e.g. Gujarat SE monthly"
                       data-testid="preset-name-input" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={newDefault}
                       onChange={e => setNewDefault(e.target.checked)}
                       data-testid="preset-default-checkbox" />
              Set as default for this report
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !newName.trim()}
                      data-testid="preset-save-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
